import type { Address } from "viem"
import { action, makeObservable, observable, runInAction } from "mobx"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenRepository } from "@/domains/tokens"
import type { TokenStore } from "@/domains/tokens/token-store"
import { LcAddress } from "@/domains/core"
import { ThickV2ApiFactory } from "@/domains/tokens/factories/thick-v2-api-factory"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { IManualClOrchestrator } from "./interfaces"
import type { PoolSwapFeeEntry } from "./transports/manual-cl-pool-on-chain-transport"
import type { ManualClPoolApiData } from "./types"
import { ManualClPoolCreatedEvent, ManualClPositionCreatedEvent } from "./events"
import { convertApiResponseToManualClPoolStoreDAO } from "./manual-cl-converter"
import { ManualClGraphQlTransport } from "./manual-cl-graphql-transport"
import { ManualClPoolRepository } from "./manual-cl-repository"
import { ManualClPoolStore } from "./manual-cl-store"

export class ManualClOrchestrator implements IManualClOrchestrator {
  private loadPoolsQuery: OrchestratedQuery<Array<ManualClPoolApiData>> | undefined
  private loadPoolFeesQuery: OrchestratedQuery<Array<PoolSwapFeeEntry>> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private poolCreatedHandler: ((event: ManualClPoolCreatedEvent) => void) | undefined
  private positionCreatedHandler: ((event: ManualClPositionCreatedEvent) => void) | undefined

  /**
   * Per-page "did we at least attempt a fetch?" flag owned by the orchestrator
   * so the detail composite can stay a pure observer (no local `useState`, no
   * `useEffect` doing data work). Seeded `false`; flips to `true` on mount and
   * back to `false` once the fetch settles.
   */
  isActiveDetailPoolResolving: boolean = false
  /** Serial token so late-returning fetches for a stale address are ignored. */
  private activeDetailPoolResolveToken: number = 0

  constructor(
    private readonly manualClPoolRepository: ManualClPoolRepository,
    private readonly manualClGraphQlTransport: ManualClGraphQlTransport,
    private readonly tokenRepository: TokenRepository,
    private readonly eventBus: IEventBus,
    private readonly poolOnChainTransport?: {
      loadPoolSwapFees: (
        pools: ReadonlyArray<{ address: Address }>,
        chainId: number,
        query: IOrchestratedQuerySender<unknown>,
      ) => Promise<PoolSwapFeeEntry[]>
    },
    private readonly chainId?: number,
  ) {
    makeObservable(this, {
      isActiveDetailPoolResolving: observable,
      setActiveDetailPoolAddress: action,
    })
  }

  initialize(
    loadPoolsQuery: OrchestratedQuery<Array<ManualClPoolApiData>>,
    loadPoolFeesQuery?: OrchestratedQuery<Array<PoolSwapFeeEntry>>,
  ) {
    this.loadPoolsQuery = loadPoolsQuery
    this.loadPoolFeesQuery = loadPoolFeesQuery

    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to pool creation events
    this.poolCreatedHandler = this.handlePoolCreated.bind(this)
    this.eventBus.subscribe(ManualClPoolCreatedEvent.TYPE, this.poolCreatedHandler)

    // Subscribe to position creation events
    this.positionCreatedHandler = this.handlePositionCreated.bind(this)
    if (this.positionCreatedHandler) {
      this.eventBus.subscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)
    }

    // Manual CL is no longer feature-flagged: start the pools sync query
    // immediately so the menu item always has data behind it.
    this.loadPoolsQuery.start()
    this.loadPoolFeesQuery?.start()
  }

  private handleWalletConnect(_event: WalletConnectedEvent): void {
    // Phase 2: Start user queries if flag enabled
    // For now, do nothing in phase 1
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Clear user data when wallet disconnects
    this.manualClPoolRepository.clearAllUserValues()
  }

  private handlePoolCreated(_event: ManualClPoolCreatedEvent): void {
    // Force refresh pool list when a new pool is created
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.forceExecute()
    }
  }

  private handlePositionCreated(_event: ManualClPositionCreatedEvent): void {
    // Force refresh pool list when a new position is created
    // This ensures pool stats (like liquidity provider count) are updated
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.forceExecute()
    }
    // TODO: When position queries are added, force refresh those too
  }

  get query(): OrchestratedQuery<Array<ManualClPoolApiData>> | undefined {
    return this.loadPoolsQuery
  }

  /**
   * Load pools from GraphQL (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadPoolsTrigger(
    _query: IOrchestratedQuerySender<Array<ManualClPoolApiData>>,
  ): Promise<Array<ManualClPoolApiData>> {
    return await this.manualClGraphQlTransport.loadManualClPools()
  }

  /**
   * Callback for when pools query succeeds
   */
  onLoadPoolsQuerySuccess(
    _sender: IOrchestratedQuerySender<Array<ManualClPoolApiData>>,
    data: Array<ManualClPoolApiData>,
  ): void {
    try {
      runInAction(() => {
        for (let i = 0; i < data.length; i++) {
          const element = data[i]

          if (element) {
            try {
              this.processPoolFromGraphQl(element)
            } catch (error) {
              console.error(
                "[ManualClOrchestrator.onLoadPoolsQuerySuccess] Error processing pool data:",
                element,
                error,
              )
            }
          }
        }
      })
      this.loadPoolFeesQuery?.forceExecute()
    } catch (error) {
      console.error("[ManualClOrchestrator.onLoadPoolsQuerySuccess] Error processing manual CL pool data:", error)
    }
  }

  /**
   * Callback for when pools query fails
   */
  onLoadPoolsQueryFailed(_sender: IOrchestratedQuerySender<Array<ManualClPoolApiData>>, error: Error): void {
    console.error("Failed to load manual CL pools", error)
  }

  async onLoadPoolFeesTrigger(
    query: IOrchestratedQuerySender<Array<PoolSwapFeeEntry>>,
  ): Promise<Array<PoolSwapFeeEntry>> {
    if (!this.poolOnChainTransport || this.chainId === undefined) return []
    const pools = this.manualClPoolRepository.getAll().map((pool) => ({
      address: pool.address.toViemAddress(),
    }))
    return await this.poolOnChainTransport.loadPoolSwapFees(pools, this.chainId, query)
  }

  onLoadPoolFeesQuerySuccess(
    _sender: IOrchestratedQuerySender<Array<PoolSwapFeeEntry>>,
    data: Array<PoolSwapFeeEntry>,
  ): void {
    runInAction(() => {
      for (const entry of data) {
        const pool = this.manualClPoolRepository.find(new LcAddress(entry.poolAddress))
        pool?.setCurrentFeeTier(entry.fee)
      }
    })
  }

  onLoadPoolFeesQueryFailed(_sender: IOrchestratedQuerySender<Array<PoolSwapFeeEntry>>, error: Error): void {
    console.error("Failed to load manual CL pool swap fees", error)
  }

  /**
   * Query pools for a specific token pair on-demand
   * Used by CreatePoolStore when tokens are selected
   *
   * @param token0 - First token (TokenStore type - provides address and chainId)
   * @param token1 - Second token (TokenStore type - provides address and chainId)
   * @returns Array of pools matching the token pair
   */
  async queryPoolsByTokenPair(token0: TokenStore, token1: TokenStore): Promise<Array<ManualClPoolApiData>> {
    return await this.manualClGraphQlTransport.loadPoolsByTokenPair(token0, token1)
  }

  /**
   * Register the pool address that the detail page currently owns. Triggers a
   * fetch through {@link queryPoolByAddress} and flips the observable
   * `isActiveDetailPoolResolving` flag for the duration so the UI can show a
   * "Loading pool…" state without reaching into wagmi / a local `useState`.
   *
   * Call with `undefined` on unmount to clear the flag.
   *
   * Uses a serial token to protect against out-of-order completions if the
   * user navigates between pools faster than the indexer responds.
   */
  setActiveDetailPoolAddress(address: Address | undefined, chainId: number): void {
    this.activeDetailPoolResolveToken += 1
    const token = this.activeDetailPoolResolveToken

    if (address === undefined) {
      this.isActiveDetailPoolResolving = false
      return
    }

    this.isActiveDetailPoolResolving = true
    this.queryPoolByAddress(address, chainId)
      .catch(() => undefined)
      .finally(() => {
        if (token !== this.activeDetailPoolResolveToken) return
        runInAction(() => {
          this.isActiveDetailPoolResolving = false
        })
      })
  }

  /**
   * Query a single pool by address
   * Used by CreatePositionStore when pool address is provided
   *
   * @param address - Pool address
   * @param chainId - Chain ID
   * @returns Pool store if found, undefined otherwise
   */
  async queryPoolByAddress(address: Address, chainId: number): Promise<ManualClPoolStore | undefined> {
    const poolAddress = new LcAddress(address)

    const found = this.manualClPoolRepository.find(poolAddress)

    if (found) {
      return found
    }

    // Always fetch fresh data from GraphQL to ensure we have currentTick
    // (repository may have stale pools created before currentTick was added)
    const graphQlData = await this.manualClGraphQlTransport.loadPoolByAddress(address, chainId)

    if (!graphQlData) {
      return undefined
    }

    // Process and add/update in repository
    runInAction(() => {
      this.processPoolFromGraphQl(graphQlData)
    })
    this.loadPoolFeesQuery?.forceExecute()

    // Return from repository (now guaranteed to have fresh data)
    return this.manualClPoolRepository.find(poolAddress)
  }

  /**
   * Close the token-API / pools-query race by materialising any tokens the
   * indexer referenced that the `TokenRepository` has not loaded yet.
   *
   * Without this step, a cold page load on an environment with no cached
   * token list (beta / first-visit production) drops every pool whose
   * tokens had not yet arrived — because the converter's "tokens not
   * found" branch returns `null` and the pool never enters the
   * repository. Once `onLoadTokenQuerySuccess` later resolves, the token
   * orchestrator enriches these placeholders in place via
   * `updateStaticData`, so observers keep the same `TokenStore` identity
   * and simply re-render with the full token metadata.
   */
  private ensurePoolTokensInRepository(graphQlData: ManualClPoolApiData): void {
    const hydrate = (tokenApi: ManualClPoolApiData["token0"] | ManualClPoolApiData["token1"]) => {
      const tokenAddress = new LcAddress(tokenApi.address)
      if (this.tokenRepository.find(tokenAddress)) {
        return
      }

      const placeholder = ThickV2ApiFactory.createToken({
        address: tokenApi.address,
        symbol: tokenApi.symbol,
        name: tokenApi.name,
        decimals: tokenApi.decimals,
      })
      this.tokenRepository.add(placeholder)
    }

    hydrate(graphQlData.token0)
    hydrate(graphQlData.token1)
  }

  /**
   * Process pool data from GraphQL and add to repository
   */
  private processPoolFromGraphQl(graphQlData: ManualClPoolApiData): void {
    this.ensurePoolTokensInRepository(graphQlData)

    const poolAddress = new LcAddress(graphQlData.address)
    const found = this.manualClPoolRepository.find(poolAddress)

    if (found) {
      // console.log("[ManualClOrchestrator.processPoolFromGraphQl] Updating existing pool:", poolAddress.toString())
      // Update existing pool
      const storeDAO = convertApiResponseToManualClPoolStoreDAO(graphQlData, this.tokenRepository)
      if (storeDAO) {
        found.updateStaticData(storeDAO)
        // console.log("[ManualClOrchestrator.processPoolFromGraphQl] Pool updated successfully")
      } else {
        console.warn(
          "[ManualClOrchestrator.processPoolFromGraphQl] Could not create DAO for update:",
          graphQlData.address,
        )
      }
      return
    }

    // console.log("[ManualClOrchestrator.processPoolFromGraphQl] Creating new pool:", poolAddress.toString())
    // Create new pool
    const storeDAO = convertApiResponseToManualClPoolStoreDAO(graphQlData, this.tokenRepository)
    if (!storeDAO) {
      console.warn(
        "[ManualClOrchestrator.processPoolFromGraphQl] Could not create store DAO for pool:",
        graphQlData.address,
      )
      return
    }

    const poolStore = new ManualClPoolStore(storeDAO)
    this.manualClPoolRepository.add(poolStore)
  }
}
